import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { pick } from "../../utils/lodash";
import {
    setVariableMentions,
    navigationNextPage,
    validationSetResultNext,
    setIsFormFocused,
    setCarrouselActiveVariableIndex,
} from "../actions";
import { StoreState } from "../../models/states/StoreState";
import { getDefaultChoices, getChoice } from "../../models/utilities/variableUtilities";
import { isVisible, shouldSetSoftPrompt } from "../../models/utilities/choiceUtilities";
import { getSoftPromptCode } from "../../models/utilities/questionUtilities";
import { ValidatorFunction, VariableErrorModel } from "../../validation/VariableErrorModel";
import { ThunkActionType } from "../actions/types";
import { evaluate, evaluateIsQuestionVisible } from "./evaluator";
import { QuestionModel } from "../../models/QuestionModel";
import { LogicModel } from "../../models/LogicModel";
import { eliminateIndexed, eliminateChoiceList } from "../../elimination/Elimination";
import { VariableModel } from "../../models/VariableModel";
import { makeMentionsSelector } from "../selectors/mentions";
import { answerRequiredValidators, questionValidators } from "../../validation/validators";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { SoftPromptChoiceType } from "../../models/enums/SoftPromptChoiceType";
import { __getStore__ } from "../store";
import softPrompt, { doesVariableExistInErrors } from "../../validation/rules/softPrompt";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { shouldIgnoreValidationErrors } from "../../models/utilities/validationUtilities";
import { HttpHeaders, QueryParameters } from "../../consts";
import { QuestionType } from "../../models/enums/QuestionType";
import { getSettingBoolean, hasSetting } from "../../models/utilities/settingUtilities";

export function makeAxios(): ThunkActionType<AxiosInstance> {
    return (dispatch, getState) => {
        const state = getState();
        const { connection } = state;

        const instance = axios.create({
            baseURL: connection.url,
            timeout: 100000,
        });

        instance.interceptors.request.use(csrfAxiosRequestInterceptor as any);
        instance.interceptors.request.use(psidAxiosRequestInterceptor as any);
        return instance;
    };
}

export function csrfAxiosRequestInterceptor(value: AxiosRequestConfig) {
    const config = value;
    const csrfToken = document.querySelector<HTMLInputElement>(
        '[name="__RequestVerificationToken"]',
    )?.value;

    if (csrfToken) {
        config.headers = config.headers || {};
        config.headers[HttpHeaders.CSRF_TOKEN] = csrfToken;
    }
    return config;
}

export function psidAxiosRequestInterceptor(value: AxiosRequestConfig) {
    const config = value;
    const psid = document.querySelector<HTMLInputElement>("#S2_SessionId")?.value;
    const skey = document.querySelector<HTMLInputElement>("#S2_SessionId_Skey")?.value;

    if (psid) {
        config.params = config.params || {};
        config.params[QueryParameters.PAGE_SESSION_ID] = psid;
    }
    if (skey) {
        config.params = config.params || {};
        config.params[QueryParameters.SESSION_KEY] = skey;
    }
    return config;
}

export function eliminateVariables(
    question: QuestionModel,
    options: { includeInvisible?: boolean; includeSoftPrompt?: boolean },
): ThunkActionType<VariableModel[]> {
    const { variableNames, variableDependencies } = question;
    return (dispatch, getState) => {
        const { interview } = getState();
        const { questionnaire, variables } = interview;

        const evaluator = (logic: LogicModel) => dispatch(evaluate(logic)).asBoolean();

        const mentions = pick(variables.mentionsByVariableName, [
            ...(variableNames || []),
            ...(variableDependencies || []),
        ]);

        const filteredVariables = eliminateIndexed<VariableModel>(
            questionnaire,
            "Variable",
            question,
            mentions,
            question.variables,
            evaluator,
        ).map((v) => {
            const choiceList = eliminateChoiceList(
                questionnaire,
                question,
                mentions,
                v.choiceList,
                evaluator,
                options,
                v,
            );
            return {
                ...v,
                choiceList,
            };
        });

        return filteredVariables;
    };
}

export function getVisibleQuestions(): ThunkActionType<QuestionModel[]> {
    const eliminationOptions = {
        includeInvisible: true,
        includeSoftPrompt: true,
    };
    return (dispatch, getState) => {
        const { interview } = getState();
        const {
            navigation: {
                currentPage: { questions },
            },
        } = interview;

        const visibleQuestions = questions
            .filter((question) => dispatch(evaluateIsQuestionVisible(question)))
            .map((question) => {
                const variables = dispatch(eliminateVariables(question, eliminationOptions));
                return {
                    ...question,
                    variables,
                };
            });

        return visibleQuestions;
    };
}

export function setDefaultInvisibleChoices(questions: QuestionModel[]): ThunkActionType {
    return (dispatch, getState) => {
        const state: StoreState = getState();
        questions.forEach((question) => {
            question.variables.forEach((variable) => {
                const { name: variableName } = variable;
                const mentionsSelector = makeMentionsSelector(variableName);
                const mentions = mentionsSelector(state);
                if (mentions.length) {
                    return;
                }

                const defaultChoices = getDefaultChoices(variable);
                const defaultInvisibleChoices = defaultChoices.filter(
                    (choice) => !isVisible(choice),
                );

                if (defaultInvisibleChoices.length) {
                    dispatch(
                        setVariableMentions(variableName, (m) => {
                            defaultInvisibleChoices.forEach((choice) => m.add(choice.value));
                            return m;
                        }),
                    );
                }
            });
        });
    };
}

export function setSoftPromptChoices(
    questions: QuestionModel[],
    softPromptType: SoftPromptChoiceType,
): ThunkActionType {
    return (dispatch, getState) => {
        const state: StoreState = getState();
        const {
            interview: {
                validation: { errorsByVariableName },
            },
        } = state;
        questions
            .filter((question) => question.settings.AnswerRequired === "SoftPrompt")
            .forEach((question) => {
                question.variables.forEach((variable) => {
                    const { name: variableName } = variable;
                    const mentionsSelector = makeMentionsSelector(variableName);
                    const mentions = mentionsSelector(state);
                    if (mentions.length) {
                        return;
                    }

                    const errors = errorsByVariableName[variableName] || [];
                    if (!errors.some((error) => error.softPrompt)) {
                        return;
                    }

                    const softPromptCode = getSoftPromptCode(question);
                    if (!softPromptCode) {
                        return;
                    }

                    const choice = getChoice(variable, softPromptCode);
                    if (choice && shouldSetSoftPrompt(choice, softPromptType)) {
                        dispatch(setVariableMentions(variableName, (m) => m.set(choice.value)));
                    }
                });
            });
    };
}

export function setSoftPromptInvisibleChoices(questions: QuestionModel[]): ThunkActionType {
    return (dispatch) => dispatch(setSoftPromptChoices(questions, SoftPromptChoiceType.Invisible));
}

export function setSoftPromptVisibleDefaultChoices(questions: QuestionModel[]): ThunkActionType {
    return (dispatch) =>
        dispatch(setSoftPromptChoices(questions, SoftPromptChoiceType.VisibleDefault));
}

export function validate(questions: QuestionModel[]): ThunkActionType<ValidationResultModel> {
    return (dispatch, getState) => {
        dispatch(setIsFormFocused(false));
        const validationResult = getValidationResults(questions, getState);
        dispatch(validationSetResultNext(validationResult));
        return validationResult;
    };
}

export function createValidationResult(variableErrors: VariableErrorModel[]) {
    const isValid =
        variableErrors.length === 0 ||
        shouldIgnoreValidationErrors(variableErrors.map((errors) => errors.error));
    return {
        isValid,
        errorsByVariableName: variableErrors.reduce((acc, { variableName, error }) => {
            if (!acc[variableName]) {
                acc[variableName] = [error];
            } else {
                acc[variableName].push(error);
            }
            return acc;
        }, {}),
    };
}

export function getValidationResults(
    questions: QuestionModel[],
    getState: () => StoreState = __getStore__().getState,
): ValidationResultModel {
    const compose = (validationErrors: VariableErrorModel[], validator: ValidatorFunction) => {
        const error = validator(getState);
        if (!error) {
            return validationErrors;
        }
        return validationErrors.concat(error);
    };

    const useClientValidation = getState().interview?.useClientValidation;
    const validationErrors = questions
        .map((question) => {
            const answerRequiredErrors = answerRequiredValidators(
                question,
                useClientValidation,
            ).reduce(compose, []);
            if (answerRequiredErrors.length) {
                return answerRequiredErrors;
            }

            const questionValidationErrors = questionValidators(
                question,
                useClientValidation,
            ).reduce(compose, []);
            return questionValidationErrors;
        })
        .reduce((acc, current) => acc.concat(current), []);

    return createValidationResult(validationErrors);
}

export function validateAndNavigateToNextPage(): ThunkActionType {
    return (dispatch) => {
        const questions = dispatch(getVisibleQuestions());

        dispatch(setDefaultInvisibleChoices(questions));
        dispatch(setSoftPromptInvisibleChoices(questions));

        const validationResult = dispatch(validate(questions));
        dispatch(setSoftPromptVisibleDefaultChoices(questions));

        if (validationResult.isValid) {
            dispatch(navigationNextPage());
        }
    };
}

/**
 * This function allows the old validation process to make use of the new soft prompt validation.
 * @deprecated Use new validation instead.
 */
export function supportValidateSoftPrompt(
    question: QuestionModel,
    variables: VariableModel[],
    dispatch: any,
    state: StoreState,
) {
    const variableErrors = (variable: VariableModel) => {
        const validator = softPrompt(question, variable);
        return validator(() => state);
    };
    let errors;
    if (question.type !== QuestionType.Carrousel) {
        errors = variables?.map(variableErrors);
    } else {
        const instanceId = `${question.id}_statement`;
        let hideVariableBackArrow = false;
        let canLoopVariable = false;
        if (hasSetting(question, "HideVariableBackArrow")) {
            hideVariableBackArrow = getSettingBoolean(question, "HideVariableBackArrow");
        }

        if (hasSetting(question, "LoopVariable")) {
            canLoopVariable = getSettingBoolean(question, "LoopVariable");
        }
        const backButtonAlwaysVisible = canLoopVariable && !hideVariableBackArrow;
        let errorVariableIndex = 0;
        const subVariables = variables?.reduce((acc, variable, index) => {
            const variableName = variable.name;
            const variableExists = doesVariableExistInErrors(variableName, state);

            if (variableExists) {
                if (!acc.includes(variable)) {
                    acc.push(variable);
                }
            }
            const nextVariable = variables[index + 1];
            if (variableExists && nextVariable && !acc.includes(nextVariable.name)) {
                acc.push(nextVariable);
            }

            return acc;
        }, []);

        if (subVariables.length === 0 && variables?.length > 0) {
            subVariables.push(variables[0]);
        }
        errorVariableIndex = question.variables.findIndex(
            (variable) => variable.name === subVariables[subVariables.length - 1].name,
        );
        errors = subVariables?.map(variableErrors);
        const newPrevBtnActive = backButtonAlwaysVisible || errorVariableIndex > 0;
        const newNextBtnActive = canLoopVariable || errorVariableIndex < variables.length - 1;
        dispatch(
            setCarrouselActiveVariableIndex(
                instanceId,
                errorVariableIndex,
                newPrevBtnActive,
                newNextBtnActive,
            ),
        );
    }
    // Only display soft prompt if all variables have failed the validation.
    if (errors.every((e) => e != null)) {
        return createValidationResult(errors);
    }
    dispatch(setSoftPromptInvisibleChoices([question]));
    dispatch(setSoftPromptVisibleDefaultChoices([question]));
    return new ValidationBuilder().build();
}
